import { environment, ERROR_API_ENDPOINT, SKS_MASTER_API_ENDPOINT, THIS_APP_NAME } from './../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ErrorHandler, Injectable } from '@angular/core';
import { GlobalErrorIf } from './global-error-if';
import * as MSG from '../share/msg/msg';
import { MsgService } from '../share/msg/msg.service';
import StackTrace from 'stacktrace-js';
import { VERSION } from '../../environments/version';
import { SentryObserve } from '../share/sentry/sentry-observe';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  /** 以前にキャッチした例外を保持する変数。連続して同じエラーログ出力をしないようにするために使う。 */
  public static prevCatchedError: any = null;

  constructor(
    private http: HttpClient,
  ) { }

  /** グローバルエラー（HTTP通信エラー以外のAngular上で発生したエラー）発生時の処理  */
  public async handleError(error: Error) {
    // HTTP通信エラーはバックエンドでSlack通知させているのでフロントは無視する
    if (error.name === 'HttpErrorResponse') return;
    const isReloaded = this.checkToChunkFailed(error);
    if (isReloaded) return;

    const stackframes: StackTrace.StackFrame[] | void = await StackTrace.fromError(error).catch(err => console.error(err));
    const stackString: string = stackframes ? this.getStackTraceString(stackframes) : JSON.stringify(error);

    // 以前キャッチした例外と同じ内容だった場合、このまま処理すると永続的にエラーをSlackに吐く場合があるので処理中断させておく。
    if (GlobalErrorHandler.prevCatchedError === stackString) {
      return;
    }
    GlobalErrorHandler.prevCatchedError = stackString;
    this.sendLog(error, stackString);
    console.error(error);
    MsgService.warningSubject.next({ msg: MSG.DANGER.SYSTEM_ERROR, title: '', timeOut: 5000 });
    SentryObserve.checkAndOutputLog('error', error);
  }

  /** ブラウザ側がキャッシュを使って存在しない古いjsファイル(****.chunk.js)を読み込もうとする時があるので、その場合に起こるエラーをハンドリングし、強制的にブラウザを更新する
   * @param {Error} error エラー内容
   * @returns {boolean} リロード実施をしたか
  */
  private checkToChunkFailed(error: Error): boolean {
    const storageData = sessionStorage.getItem('reloadCountOnNoChunkError');
    /** 何らかの理由で更新の無限ループが起きないようにsessionStorageで更新回数を保持する */
    let reloadCountOnNoChunkError = 0;
    if (storageData) {
      reloadCountOnNoChunkError = Number(storageData);
    }
    // 分割したjsファイルの読込に失敗した際のエラーメッセージ"Loading chunk *** failed"を正規表現で判定
    const chunkFailedMessage = /Loading chunk [\d]+ failed/;
    if (chunkFailedMessage.test(error.message)) {
      if (reloadCountOnNoChunkError === 0) {
        sessionStorage.setItem('reloadCountOnNoChunkError', String(reloadCountOnNoChunkError + 1));
        // 引数にtrue指定でハードリロードを実行
        window.location.reload(true);
        return true;
      } else {
        return false;
      }
    }
  }

  /** スタックトレースをstringに変換
   * @param {any} stackframes スタックトレース
   * @returns {string} スタックトレース(文字列)
   */
  private getStackTraceString(stackframes: any): string {
    return stackframes.splice(0, 20).map(function(sf) {
                        return sf.toString();
                      }).join('\n');
  }

  /** ログ送信 */
  private sendLog(error: Error, stackString: string) {
    const tokenUsernameKey: string = Object.keys(localStorage).find(key => /^CognitoIdentityServiceProvider\..+\.LastAuthUser$/.test(key));
    const logs: GlobalErrorIf.PostLogs = {
      level: 'Error',
      message: error.message,
      logger: error.name,
      token_username: tokenUsernameKey ? localStorage.getItem(tokenUsernameKey) : null,
      exception: stackString,
    };
    this.sendLogToErrorAPI(logs).subscribe(
      res => {},
      err => console.error(err)
    );
  }

  /** Slackへログ出力 */
  private sendLogToErrorAPI(log: GlobalErrorIf.PostLogs) {
    const userAgent: string = window.navigator.userAgent;
    const platform: string = window.navigator.platform;
    const vendor: string = window.navigator.vendor;
    const body = {
      level: log.level,
      source: "Angular-smile9",
      message: log.message,
      exception: log.exception,
      memo: `{ "token_username": "${log.token_username}", "platform": "${platform}", "userAgent": "${userAgent}}", "vendor": "${vendor}", "location.href": "${location.href}", "angular_version": "${VERSION}" }`,
    };
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
    return this.http.post(`${ERROR_API_ENDPOINT}/api/Smile9Error`, body, options);
  }
}


